import React, { useEffect, useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { getRegistrations, resendRegistration, deleteRegistration } from '../services/api';
import { jsPDF } from 'jspdf';

export default function RegistrationForms() {
  const { t, lang } = useLang();
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReg, setSelectedReg] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    getRegistrations().then(res => setRegistrations(res.data)).catch(() => {});
  };

  const filtered = registrations.filter(r => {
    const matchSearch = !search ||
      r.recipientName?.includes(search) ||
      r.recipientEmail?.includes(search) ||
      r.courseName?.includes(search) ||
      r.submittedData?.nationalId?.includes(search);
    const matchStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const getStatusBadge = (status: string) => {
    const map: Record<string, { cls: string; label: string }> = {
      sent: { cls: 'badge-blue', label: lang === 'ar' ? 'تم الإرسال' : 'נשלח' },
      submitted: { cls: 'badge-green', label: lang === 'ar' ? 'تم التعبئة والتوقيع' : 'ממולא ונחתם' },
      expired: { cls: 'badge-red', label: lang === 'ar' ? 'منتهي الصلاحية' : 'פג תוקף' },
    };
    const s = map[status] || { cls: 'badge-gray', label: status };
    return <span className={`badge ${s.cls}`}>{s.label}</span>;
  };

  const handleResend = async (id: string) => {
    await resendRegistration(id);
    loadData();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(lang === 'ar' ? 'هل أنت متأكد من الحذف؟' : 'האם אתה בטוח?')) {
      await deleteRegistration(id);
      loadData();
    }
  };

  const handleConvertToStudent = (reg: any) => {
    alert(lang === 'ar' ? 'تم نقل البيانات إلى وحدة الطلاب' : 'הנתונים הועברו למודול סטודנטים');
  };

  const exportToExcel = () => {
    let csv = '\uFEFF';
    csv += 'שם,ת.ז.,מסלול,תאריך שליחה,תאריך מילוי,סטטוס\n';
    filtered.forEach(r => {
      const statusLabels: Record<string, string> = { sent: 'נשלח', submitted: 'ממולא ונחתם', expired: 'פג תוקף' };
      csv += `${r.recipientName},${r.submittedData?.nationalId || '-'},${r.courseName},${r.sentAt?.split('T')[0] || '-'},${r.submittedAt?.split('T')[0] || '-'},${statusLabels[r.status] || r.status}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'registration_forms.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToPdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Registration Forms Report', 105, 15, { align: 'center' });
    doc.setFontSize(10);
    let y = 30;
    doc.text('Name', 15, y); doc.text('Course', 65, y); doc.text('Sent', 115, y); doc.text('Filled', 145, y); doc.text('Status', 175, y);
    y += 8;
    doc.line(15, y - 3, 195, y - 3);
    filtered.forEach(r => {
      if (y > 270) { doc.addPage(); y = 20; }
      const statusLabels: Record<string, string> = { sent: 'Sent', submitted: 'Signed', expired: 'Expired' };
      doc.text(r.recipientName || '-', 15, y);
      doc.text(r.courseName || '-', 65, y);
      doc.text(r.sentAt?.split('T')[0] || '-', 115, y);
      doc.text(r.submittedAt?.split('T')[0] || '-', 145, y);
      doc.text(statusLabels[r.status] || r.status, 175, y);
      y += 7;
    });
    doc.save('registration_forms.pdf');
  };

  return (
    <div>
      <div className="page-section">
        <div className="page-title">
          <h2>{lang === 'ar' ? 'نماذج التسجيل' : 'טפסי רישום'}</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline" onClick={exportToExcel}>{t('exportExcel')}</button>
            <button className="btn btn-outline" onClick={exportToPdf}>{t('exportPDF')}</button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <input className="form-input" placeholder={t('search')} value={search}
            onChange={e => setSearch(e.target.value)} style={{ maxWidth: 250 }} />
          <select className="form-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ maxWidth: 180 }}>
            <option value="all">{t('all')}</option>
            <option value="sent">{lang === 'ar' ? 'تم الإرسال' : 'נשלח'}</option>
            <option value="submitted">{lang === 'ar' ? 'تم التعبئة والتوقيع' : 'ממולא ונחתם'}</option>
            <option value="expired">{lang === 'ar' ? 'منتهي الصلاحية' : 'פג תוקף'}</option>
          </select>
        </div>

        {/* Table */}
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>{t('nationalId')}</th>
                <th>{lang === 'ar' ? 'المساق' : 'מסלול'}</th>
                <th>{lang === 'ar' ? 'تاريخ الإرسال' : 'תאריך שליחה'}</th>
                <th>{lang === 'ar' ? 'تاريخ التعبئة' : 'תאריך מילוי'}</th>
                <th>{t('status')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id}>
                  <td>{r.recipientName}</td>
                  <td dir="ltr">{r.submittedData?.nationalId || r.prefillData?.nationalId || '-'}</td>
                  <td>{r.courseName}</td>
                  <td>{r.sentAt?.split('T')[0] || '-'}</td>
                  <td>{r.submittedAt?.split('T')[0] || '-'}</td>
                  <td>{getStatusBadge(r.status)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button className="btn btn-sm btn-outline" onClick={() => setSelectedReg(r)}>
                        {lang === 'ar' ? 'عرض' : 'צפייה'}
                      </button>
                      {r.status === 'submitted' && (
                        <button className="btn btn-sm btn-outline" onClick={() => {
                          const url = `${window.location.origin}/register/${r.id}`;
                          window.open(url, '_blank');
                        }}>
                          PDF
                        </button>
                      )}
                      {r.status !== 'submitted' && (
                        <button className="btn btn-sm" style={{ background: '#FF9800', color: '#fff', border: 'none' }}
                          onClick={() => handleResend(r.id)}>
                          {lang === 'ar' ? 'إعادة إرسال' : 'שלח שוב'}
                        </button>
                      )}
                      {r.status === 'submitted' && (
                        <button className="btn btn-sm" style={{ background: '#4CAF50', color: '#fff', border: 'none' }}
                          onClick={() => handleConvertToStudent(r)}>
                          {lang === 'ar' ? 'نقل للطلاب' : 'העבר לסטודנט'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Registration Modal */}
      {selectedReg && (
        <div className="modal-overlay" onClick={() => setSelectedReg(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h3 className="modal-title">{lang === 'ar' ? 'تفاصيل نموذج التسجيل' : 'פרטי טופס רישום'}</h3>
              <button className="modal-close" onClick={() => setSelectedReg(null)}>&times;</button>
            </div>
            <div style={{ padding: 16 }}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('name')}</label>
                  <div className="form-input" style={{ background: '#f8f9fa' }}>{selectedReg.recipientName}</div>
                </div>
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'المساق' : 'מסלול'}</label>
                  <div className="form-input" style={{ background: '#f8f9fa' }}>{selectedReg.courseName}</div>
                </div>
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'الرسوم' : 'שכ"ל'}</label>
                  <div className="form-input" style={{ background: '#f8f9fa' }}>{selectedReg.tuitionAmount?.toLocaleString()} &#x20AA;</div>
                </div>
                <div className="form-group">
                  <label className="form-label">{t('status')}</label>
                  <div>{getStatusBadge(selectedReg.status)}</div>
                </div>
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'تاريخ الإرسال' : 'תאריך שליחה'}</label>
                  <div className="form-input" style={{ background: '#f8f9fa' }}>{selectedReg.sentAt?.split('T')[0] || '-'}</div>
                </div>
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'تاريخ التعبئة' : 'תאריך מילוי'}</label>
                  <div className="form-input" style={{ background: '#f8f9fa' }}>{selectedReg.submittedAt?.split('T')[0] || '-'}</div>
                </div>
              </div>
              {selectedReg.submittedData && (
                <>
                  <h4 style={{ marginTop: 16, marginBottom: 8 }}>{lang === 'ar' ? 'البيانات المعبأة' : 'נתונים שמולאו'}</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{t('nationalId')}</label>
                      <div className="form-input" style={{ background: '#f8f9fa' }}>{selectedReg.submittedData.nationalId}</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('phone')}</label>
                      <div className="form-input" style={{ background: '#f8f9fa' }}>{selectedReg.submittedData.phone}</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('email')}</label>
                      <div className="form-input" style={{ background: '#f8f9fa' }} dir="ltr">{selectedReg.submittedData.email}</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">{lang === 'ar' ? 'طريقة الدفع' : 'אמצעי תשלום'}</label>
                      <div className="form-input" style={{ background: '#f8f9fa' }}>{selectedReg.submittedData.paymentMethod}</div>
                    </div>
                  </div>
                </>
              )}
              {selectedReg.signatureData && selectedReg.signatureData.length > 50 && (
                <div style={{ marginTop: 16 }}>
                  <label className="form-label">{lang === 'ar' ? 'التوقيع' : 'חתימה'}</label>
                  <img src={selectedReg.signatureData} alt="signature" style={{ border: '1px solid #ddd', borderRadius: 8, maxWidth: 300 }} />
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setSelectedReg(null)}>{t('close')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
